import type { FC } from 'react';
import { cn } from '../lib/utils';

/**
 * The three pieces of the Ziggiz mark, ordered by the sequence they land in.
 * Paths are lifted verbatim from `public/ziggiz-logo.svg`.
 *
 * `--z-loader-fall` is how far above its slot the piece starts, in viewBox
 * units; later pieces fall further so the stack builds from the base up.
 */
const LAYERS = [
  {
    // outer chevron — lands first, carries the stack
    d: 'M271.238 170.421L240.731 139.926V200.882L210.224 231.377L179.717 261.873L149.21 292.368L118.703 261.873L88.1955 231.377L57.6885 200.882V139.926L27.1815 170.421L2.34147 195.251C-0.780491 198.372 -0.780491 203.426 2.34147 206.513L27.1815 231.344L57.6885 261.839L88.1955 292.334L118.703 322.829L143.543 347.659C146.665 350.78 151.721 350.78 154.809 347.659L179.649 322.829L210.156 292.334L240.663 261.839L271.17 231.344L296.146 206.377C299.2 203.325 299.2 198.406 296.146 195.353L271.17 170.387L271.238 170.421Z',
    className: '[--z-loader-fall:150px] [animation-delay:0ms]'
  },
  {
    // inner chevron — settles into the outer
    d: 'M88.2966 170.421L118.804 200.882L149.277 231.377L179.784 200.882L210.257 170.421L240.764 139.926L210.257 109.465L179.784 78.9693V139.926L149.277 170.421L118.804 139.926V78.9693L88.2966 109.465L57.7896 139.926L88.2966 170.421Z',
    className: '[--z-loader-fall:190px] [animation-delay:160ms]'
  },
  {
    // cap — falls furthest, lands last
    d: 'M179.784 78.9691L154.944 54.1388C151.822 51.018 146.766 51.018 143.678 54.1388L118.838 78.9691L149.345 109.464L179.852 78.9691H179.784Z',
    className: '[--z-loader-fall:240px] [animation-delay:320ms]'
  }
] as const;

/**
 * The mark's bounding box is `0 51.8 298.44 298.2`. This adds 60 units of
 * runway above and below so a falling piece is visible in flight without
 * overflowing the element and colliding with whatever sits around it.
 */
const MARK_VIEW_BOX = '0 -8.2 298.44 418.2';

interface ZiggizLoaderProps {
  /** Announced by the live region; also the visible caption when `showLabel`. */
  label?: string;
  /** Render `label` under the mark instead of screen-reader only. */
  showLabel?: boolean;
  /** Applied to the mark. Set a width (`w-16`) to size it, `text-*` to recolour. */
  className?: string;
}

/**
 * Indeterminate loader: the pieces of the Ziggiz mark drop in one at a time
 * and stack into the logo, then fall away and do it again.
 */
export const ZiggizLoader: FC<ZiggizLoaderProps> = ({
  label = 'Loading',
  showLabel = false,
  className
}) => (
  <span role="status" className="inline-flex flex-col items-center gap-3">
    <svg
      viewBox={MARK_VIEW_BOX}
      fill="currentColor"
      aria-hidden="true"
      className={cn(
        'h-auto w-11 shrink-0 text-brand drop-shadow-[0_0_12px_var(--z-brand-border)]',
        className
      )}
    >
      {LAYERS.map((layer) => (
        <path
          key={layer.className}
          d={layer.d}
          className={cn('ziggiz-loader-layer', layer.className)}
        />
      ))}
    </svg>
    <span
      className={
        showLabel
          ? 'text-caption font-medium tracking-[0.08em] text-muted-foreground uppercase'
          : 'sr-only'
      }
    >
      {label}
    </span>
  </span>
);
